import { createFileRoute } from '@tanstack/react-router';
import { Badge, Button, Card, Group, Loader, Stack, Table, Text, Title } from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { notifyError } from '@/shared/lib/notify';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/providers/AuthProvider';
import { STORAGE_KEYS } from '@/shared/lib/constants';
import * as devicesApi from '@/features/users/devices';
import { isMcpDevice } from '@/features/mcp/api';
import dayjs from 'dayjs';
import { Paginated } from '@/shared/components/DataTable/TablePagination';

import { leggiChiave } from '@/shared/lib/storage';
export const Route = createFileRoute('/_app/settings/devices')({ component: DevicesPage });

function currentFingerprint(): string | null {
  try { return leggiChiave(STORAGE_KEYS.fingerprint); } catch { return null; }
}

function DevicesPage() {
  const { decoded, user } = useAuth();
  const userId = decoded?.userId ?? decoded?.sub ?? user?.id ?? '';
  const myHash = currentFingerprint();
  const qc = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['my-devices', userId],
    queryFn: () => devicesApi.fetchMyDevices(userId),
    enabled: Boolean(userId),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['my-devices', userId] });

  const disconnectOne = useMutation({
    mutationFn: (deviceHash: string) => devicesApi.disconnectOneDevice({ userId, deviceHash }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Dispositivo disconnesso' });
      invalidate();
    },
    onError: (err) => notifyError(err),
  });

  const disconnectAll = useMutation({
    mutationFn: () => devicesApi.disconnectAllDevices({ userId, currentDeviceHash: myHash }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Altri dispositivi disconnessi' });
      invalidate();
    },
    onError: (err) => notifyError(err),
  });

  const askDisconnectAll = () =>
    modals.openConfirmModal({
      title: 'Disconnetti tutti i dispositivi',
      children: (
        <Text size="sm">
          Verrai disconnesso da tutti i dispositivi tranne quello corrente.
          {hiddenAssistants > 0 ? ` Vengono revocati anche ${hiddenAssistants === 1 ? 'l’assistente AI collegato' : `i ${hiddenAssistants} assistenti AI collegati`} (Claude): per usarli di nuovo andranno ricollegati.` : ''} Confermi?
        </Text>
      ),
      labels: { confirm: 'Disconnetti tutti', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: () => void disconnectAll.mutate(),
    });

  /* Il dispositivo che il BE crea per un assistente collegato (MCP) non è un telefono né un browser:
     qui sarebbe una riga incomprensibile. Gli assistenti stanno in Impostazioni › Integrazioni
     (`docs/FE-TO-BE-MCP-v1.md` §5). ⚠️ «Disconnetti tutti» però li revoca (ruota il salt di ogni
     dispositivo, BE-DONE-MCP-v1 §1): la conferma lo dice, contandoli. */
  const all = data ?? [];
  const rows = all.filter((d) => !isMcpDevice(d));
  const hiddenAssistants = all.length - rows.length;
  const others = rows.filter((d) => d.deviceHash !== myHash).length;

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Dispositivi attivi</Title>
        <Button color="red" variant="light" onClick={askDisconnectAll} disabled={others === 0 || disconnectAll.isPending} loading={disconnectAll.isPending}>
          Disconnetti tutti gli altri
        </Button>
      </Group>
      <Card withBorder padding={0} radius="lg">
        {isError ? (
        <QueryErrorAlert error={error} onRetry={() => void refetch()} />
      ) : isLoading ? (
          <Group p="lg"><Loader size="sm" /></Group>
        ) : (
          <Paginated rows={rows} label="dispositivi">
          {(pageRows) => (
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Dispositivo</Table.Th>
                <Table.Th>Piattaforma</Table.Th>
                <Table.Th>Ultimo accesso</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pageRows.map((d) => {
                const isCurrent = Boolean(myHash) && d.deviceHash === myHash;
                const label = d.deviceName?.trim() || d.platform?.trim() || (d.deviceHash ? `${d.deviceHash.slice(0, 12)}…` : 'Dispositivo');
                return (
                  <Table.Tr key={d.id}>
                    <Table.Td>
                      <Group gap={8} wrap="nowrap">
                        <Text size="sm">{label}</Text>
                        {isCurrent && <Badge size="xs" color="green" variant="light">Questo dispositivo</Badge>}
                      </Group>
                    </Table.Td>
                    <Table.Td>{d.platform?.trim() || '—'}</Table.Td>
                    <Table.Td>{d.lastAccess ? dayjs(d.lastAccess).format('DD/MM/YYYY HH:mm') : '—'}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        disabled={isCurrent || !d.deviceHash || disconnectOne.isPending}
                        onClick={() => d.deviceHash && disconnectOne.mutate(d.deviceHash)}
                      >
                        {isCurrent ? 'Sessione corrente' : 'Disconnetti'}
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center" py="lg">Nessun dispositivo registrato</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          )}
          </Paginated>
        )}
      </Card>
    </Stack>
  );
}
